import {
  forwardRef, useId, useImperativeHandle, useRef,
  type InputHTMLAttributes, type MouseEvent, type ReactNode,
} from 'react'
import { cx } from './cx'
import s from './UnitInput.module.css'

export interface UnitInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  /** Set before the value, inside the slot: "€". */
  prefix?: ReactNode
  /** Set after the value, inside the slot: "users", "EUR", "days". */
  unit?: ReactNode
}

/**
 * A VALUE WITH ITS UNIT, INSIDE ONE SLOT (#2406, "Input Controls and
 * Validation": "Seats 1,500 users", "Annual value € 184,000 EUR").
 *
 * The unit is PART OF THE SLOT — muted, weight 700 — not a label beside a box
 * and not text typed into the value. That is the difference that matters: a
 * unit outside the slot reads as a second field; a unit inside the value
 * ("1500 users") is a string no number parser accepts. The value keeps its own
 * alignment and the input its own width; the affixes take only what they need.
 *
 * ── one control, still ─────────────────────────────────────────────────────
 * The `<input>` is the control. Everything a `Field` clones onto its child —
 * `id`, `aria-describedby`, `aria-invalid`, `aria-required` — lands on it,
 * because they arrive in `rest` and are spread there. The affixes are named
 * into its DESCRIPTION, so a reader hears "Seats, 1,500, users" rather than a
 * bare number whose unit only the sighted get. Pressing an affix focuses the
 * input, as pressing the slot's padding would on a plain field.
 *
 * ── the states are the value slot's ────────────────────────────────────────
 * Rest, hover, focus (ring + vein), invalid, read-only and disabled are
 * `Input.module.css`'s, restated on the WRAPPER through `:has()` because the
 * slot is the wrapper now and the input inside it is transparent.
 */
export const UnitInput = forwardRef<HTMLInputElement, UnitInputProps>(function UnitInput(
  { prefix, unit, className, style, ...rest },
  ref,
) {
  const input = useRef<HTMLInputElement>(null)
  useImperativeHandle(ref, () => input.current as HTMLInputElement)
  const auto = useId()
  const prefixId = `${auto}-prefix`
  const unitId = `${auto}-unit`
  const describedBy = [
    rest['aria-describedby'],
    prefix != null ? prefixId : null,
    unit != null ? unitId : null,
  ].filter(Boolean).join(' ') || undefined

  /* A press on the slot's own ground (an affix, the padding) focuses the
     value. `preventDefault` on mousedown keeps the focus from bouncing to the
     wrapper first; a press on the input itself is left alone. */
  const focusValue = (event: MouseEvent<HTMLSpanElement>) => {
    if (event.target === input.current || rest.disabled) return
    event.preventDefault()
    input.current?.focus()
  }

  return (
    // The wrapper forwards a pointer press to the input it wraps; the input
    // is the control and remains the keyboard path, so no role belongs here.
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <span className={cx(s.slot, className)} style={style} data-unit-input="" onMouseDown={focusValue}>
      {prefix != null && <span className={s.affix} id={prefixId}>{prefix}</span>}
      <input {...rest} ref={input} className={s.input} aria-describedby={describedBy} />
      {unit != null && <span className={s.affix} id={unitId}>{unit}</span>}
    </span>
  )
})
